import type {
  AdaptationReason, CalibrationSummary, Difficulty, FailureType, SelfEstimate, Skill, SkillEstimate, SkillView, SubjectId,
} from '@/core/types';
import { catalog } from '@/data/catalog';
import { uid } from '@/core/util/misc';
import { expectedInfoGain, gaussian, mean, sd } from './ability';
import { domainDistance, skillRelevance, stageDistance, type EligibilityMap } from './eligibility';
import { readinessNarrative, subjectReadiness } from './readiness';
import { B, L, loc, LL } from '@/i18n';

/**
 * ADAPTIVE CALIBRATION
 * Broad sampling → ability boundary → prerequisite probing → readiness estimate.
 *
 * The self-estimate only sets where the search STARTS (a selection prior).
 * It is never written into any skill estimate. Every estimate comes from the
 * answers given here (as 'diagnostic' evidence) through the shared pipeline.
 */

const se = (offset: number, difficulty: number, label: [string, string], blurb: [string, string]) => loc({ offset, difficulty, label: B(...label), blurb: B(...blurb) });

export const SELF_ESTIMATE_START: Record<SelfEstimate, { offset: number; difficulty: number; label: string; blurb: string }> = {
  rebuild: se(-3, 2, ['Beginner / rebuild', 'Nybegynner / bygge opp'], ['I want to rebuild the basics.', 'Jeg vil bygge opp det grunnleggende.']),
  developing: se(-1, 3, ['Developing', 'Under utvikling'], ['I manage some of it, with gaps.', 'Jeg klarer noe, men har hull.']),
  comfortable: se(0, 3, ['Comfortable', 'Trygg'], ['I keep up with my grade.', 'Jeg henger med på trinnet mitt.']),
  advanced: se(0, 4, ['Advanced', 'Avansert'], ['I find most of my grade easy.', 'Jeg synes det meste på trinnet er lett.']),
  ahead: se(0, 5, ['Ahead of grade', 'Foran trinnet'], ['I already work beyond my grade.', 'Jeg jobber allerede over trinnet mitt.']),
  'very-advanced': se(1, 6, ['Very advanced', 'Svært avansert'], ['I work well above my grade.', 'Jeg jobber langt over trinnet mitt.']),
  unsure: se(0, 3, ['Not sure', 'Vet ikke'], ['Let Studio find out.', 'La Studio finne ut av det.']),
};

export const LENGTHS = { quick: 10, standard: 16, thorough: 24 } as const;

export interface DomainCalState {
  domainId: string;
  workingDifficulty: number;
  workingOffset: number;
  asked: number;
  successes: number;
  failures: number;
  lo: number | null;
  hi: number | null;
  resolved: boolean;
}

export interface ProbeItem {
  skillId: string;
  difficulty: number;
  reason: AdaptationReason;
  fromSkillId?: string;
  depth?: number;
}

export interface SubjectCalState {
  subjectId: SubjectId;
  selfEstimate: SelfEstimate;
  startDifficulty: number;
  startOffset: number;
  maxQuestions: number;
  asked: number;
  domains: Record<string, DomainCalState>;
  domainOrder: string[];
  priority: Record<string, number>;
  probeQueue: ProbeItem[];
  askedSkills: Record<string, number>;
  failureTypes: Record<string, FailureType>;
  pendingFailure: Record<string, { difficulty: number; misconceptionId?: string; stepDownTried: boolean }>;
  revisit: string[];
  gaps: { skillId: string; prerequisiteId: string }[];
  done: boolean;
}

export interface AdaptationDecision {
  reason: AdaptationReason;
  direction: 'up' | 'down' | 'same' | 'probe' | 'switch';
  message: string;
  skillId: string;
  difficulty: Difficulty;
  domainId: string;
}

export interface CalibrationRun {
  id: string;
  startedAt: number;
  subjectQueue: SubjectId[];
  current: number;
  perSubject: Partial<Record<SubjectId, SubjectCalState>>;
  status: 'running' | 'completed';
  lastDecision?: AdaptationDecision;
  /** The item currently being asked (the decision that selected it). */
  pending?: AdaptationDecision;
}

/* ------------------------------------------------------------------ */

function eligibleSkills(subjectId: SubjectId, elig: EligibilityMap, domainId?: string): Skill[] {
  return catalog.skillsOf(subjectId).filter((s) => {
    if (domainId && s.domainId !== domainId) return false;
    const r = skillRelevance(s, elig[subjectId]);
    return r === 'core' || r === 'foundation' || r === 'extension';
  });
}

export function startCalibration(
  subjects: SubjectId[], selfEstimates: Partial<Record<SubjectId, SelfEstimate>>, elig: EligibilityMap, length: keyof typeof LENGTHS, now: number,
): CalibrationRun {
  const perSubject: CalibrationRun['perSubject'] = {};
  for (const sid of subjects) {
    const se = selfEstimates[sid] ?? 'unsure';
    const start = SELF_ESTIMATE_START[se];
    const maxQ = LENGTHS[length] + (se === 'unsure' ? 2 : 0);
    const domains = catalog.domainsOf(sid).filter((d) => d.calibrate && eligibleSkills(sid, elig, d.id).length);
    // Domains matter most where they meet the curriculum anchor (this is RELEVANCE, not ability).
    const near = (dist: number) => (dist === 0 ? 3 : dist === -1 ? 2 : dist === -2 ? 1 : dist > 0 ? 0.3 : 0.4);
    const priority = (dId: string) => eligibleSkills(sid, elig, dId).reduce((acc, s) => acc + s.weight * near(stageDistance(s, elig[sid])), 0);
    const ordered = [...domains].sort((a, b) => priority(b.id) - priority(a.id)).map((d) => d.id);
    const pr = Object.fromEntries(ordered.map((id) => [id, priority(id)]));
    const maxPr = Math.max(1, ...Object.values(pr));
    perSubject[sid] = {
      subjectId: sid,
      selfEstimate: se,
      startDifficulty: start.difficulty,
      startOffset: start.offset,
      maxQuestions: maxQ,
      asked: 0,
      domains: Object.fromEntries(ordered.map((id) => [id, {
        domainId: id, workingDifficulty: start.difficulty, workingOffset: start.offset, asked: 0, successes: 0, failures: 0, lo: null, hi: null, resolved: false,
      }])),
      domainOrder: ordered.slice(0, Math.min(ordered.length, Math.max(4, Math.round(maxQ / 2.3)))),
      priority: Object.fromEntries(Object.entries(pr).map(([k, v]) => [k, v / maxPr])),
      probeQueue: [],
      askedSkills: {},
      failureTypes: {},
      pendingFailure: {},
      revisit: [],
      gaps: [],
      done: false,
    };
  }
  return { id: uid('cal'), startedAt: now, subjectQueue: subjects, current: 0, perSubject, status: 'running' };
}

export function currentSubject(run: CalibrationRun): SubjectCalState | null {
  const sid = run.subjectQueue[run.current];
  return sid ? run.perSubject[sid] ?? null : null;
}

/** Selection prior for an untested skill: built from the domain's working state, never stored. */
function selectionPosterior(est: SkillEstimate | undefined, workingDifficulty: number) {
  if (est && est.evidenceCount > 0) return est.posterior;
  return gaussian(workingDifficulty, 1.4);
}

function pickSkillForDomain(
  sub: SubjectCalState, dom: DomainCalState, elig: EligibilityMap, views: Record<string, SkillView>,
): Skill | null {
  const e = elig[sub.subjectId];
  const candidates = eligibleSkills(sub.subjectId, elig, dom.domainId).filter((s) => {
    if (dom.workingDifficulty < s.difficultyRange[0] - 1) return false;
    const v = views[s.id];
    if ((sub.askedSkills[s.id] ?? 0) >= 2) return false;
    // skip skills already confidently known (avoid wasting questions on obvious mastery)
    if (v && v.evidenceCount >= 3 && v.uncertainty < 0.9) return false;
    return true;
  });
  if (!candidates.length) return null;
  const score = (s: Skill) => {
    const dist = Math.abs(domainDistance(s, elig) - dom.workingOffset);
    const asked = sub.askedSkills[s.id] ?? 0;
    const inferred = views[s.id]?.inferredSupport ? 1.5 : 0;
    const rel = skillRelevance(s, e);
    return dist * 2 + asked * 1.5 + inferred - s.weight * 0.3 - (rel === 'core' ? 0.4 : 0);
  };
  return [...candidates].sort((a, b) => score(a) - score(b))[0];
}

function bestPrerequisite(skillId: string, sub: SubjectCalState, elig: EligibilityMap, views: Record<string, SkillView>): Skill | null {
  const prereqs = catalog.prerequisites(skillId)
    .map((id) => catalog.skill(id)!)
    .filter((p) => {
      const r = skillRelevance(p, elig[p.subjectId]);
      if (!(r === 'core' || r === 'foundation' || r === 'extension')) return false;
      if ((sub.askedSkills[p.id] ?? 0) >= 2) return false;
      const v = views[p.id];
      return !v || v.evidenceCount === 0 || v.uncertainty > 1.0 || v.status === 'WEAK';
    });
  if (!prereqs.length) return null;
  // most informative: unknown first, then most central
  return prereqs.sort((a, b) => {
    const ua = (views[a.id]?.evidenceCount ?? 0) === 0 ? 0 : 1;
    const ub = (views[b.id]?.evidenceCount ?? 0) === 0 ? 0 : 1;
    return ua - ub || b.weight - a.weight;
  })[0];
}

const clampD = (s: Skill, d: number) => Math.min(s.difficultyRange[1], Math.max(s.difficultyRange[0], Math.round(d))) as Difficulty;

/**
 * Decide the next item. Returns null when the current subject is finished
 * (the caller then completes it and moves on).
 */
export function nextItem(
  runIn: CalibrationRun, elig: EligibilityMap, views: Record<string, SkillView>, estimates: Record<string, SkillEstimate>,
): { run: CalibrationRun; decision: AdaptationDecision | null } {
  const run: CalibrationRun = JSON.parse(JSON.stringify(runIn));
  const decision = decideNext(run, elig, views, estimates);
  run.pending = decision ?? undefined;
  return { run, decision };
}

function decideNext(
  run: CalibrationRun, elig: EligibilityMap, views: Record<string, SkillView>, estimates: Record<string, SkillEstimate>,
): AdaptationDecision | null {
  const sub = currentSubject(run);
  if (!sub || sub.done) return null;
  if (sub.asked >= sub.maxQuestions) return null;

  // 1. Pending probes (step-down, prerequisite, revisit) come first.
  while (sub.probeQueue.length) {
    const p = sub.probeQueue.shift()!;
    const skill = catalog.skill(p.skillId);
    if (!skill) continue;
    const domainId = skill.domainId;
    const msg = p.reason === 'prerequisite-probe'
      ? LL(`Hmm, this area is unclear. Checking a basic skill — ${skill.name} — first.`, `Hmm, dette området er uklart. Sjekker en grunnferdighet — ${skill.name} — først.`, { detailed: [`This area looks less certain. Checking a prerequisite — ${skill.name} — before continuing.`, `Dette området er litt usikkert. Sjekker en forkunnskap — ${skill.name} — før vi fortsetter.`] })
      : p.reason === 'step-down'
        ? LL(`Let's try ${skill.name} at an easier level to find your edge.`, `La oss prøve ${skill.name} på et lettere nivå for å finne grensen din.`, { detailed: [`Let's check ${skill.name} at a more familiar level to see where the boundary is.`, `La oss sjekke ${skill.name} på et mer kjent nivå for å finne grensen.`] })
        : LL(`Back to ${skill.name} — your answers there are still mixed.`, `Tilbake til ${skill.name} — svarene der er fortsatt blandet.`, { detailed: [`Revisiting ${skill.name} — the evidence there is still mixed.`, `Ser på ${skill.name} igjen — svarene der er fortsatt blandet.`] });
    return { reason: p.reason, direction: p.reason === 'revisit' ? 'same' : p.reason === 'step-down' ? 'down' : 'probe', message: msg, skillId: skill.id, difficulty: clampD(skill, p.difficulty), domainId };
  }

  // 2. Broad sampling: one item in each priority domain.
  for (const dId of sub.domainOrder) {
    const dom = sub.domains[dId];
    if (dom.asked === 0) {
      const skill = pickSkillForDomain(sub, dom, elig, views);
      if (!skill) {
        dom.resolved = true;
        continue;
      }
      const first = sub.asked === 0;
      return {
        reason: 'broad-sample', direction: 'switch',
        message: first
          ? LL(`Starting near your own guess. First area: ${catalog.domain(dId)!.name}.`, `Starter nær gjetningen din. Første område: ${catalog.domain(dId)!.name}.`, { detailed: [`Starting near your self-estimate. First area: ${catalog.domain(dId)!.name}.`, `Starter nær det du tror selv. Første område: ${catalog.domain(dId)!.name}.`] })
          : LL(`New area: ${catalog.domain(dId)!.name}. A mistake here never lowers the other areas.`, `Nytt område: ${catalog.domain(dId)!.name}. En feil her senker aldri de andre områdene.`, { detailed: [`Mapping a new area: ${catalog.domain(dId)!.name}. One mistake in one area never lowers the others.`, `Kartlegger et nytt område: ${catalog.domain(dId)!.name}. En feil i ett område senker aldri de andre.`] }),
        skillId: skill.id, difficulty: clampD(skill, dom.workingDifficulty), domainId: dId,
      };
    }
  }

  // 3. Revisit uncertain skills late in the run.
  if (sub.revisit.length && sub.asked >= Math.floor(sub.maxQuestions * 0.6)) {
    const id = sub.revisit.shift()!;
    const skill = catalog.skill(id)!;
    const est = estimates[id];
    const d = est ? Math.round(mean(est.posterior)) : 3;
    return { reason: 'revisit', direction: 'same', message: LL(`Back to ${skill.name} — earlier answers there were mixed.`, `Tilbake til ${skill.name} — tidligere svar der var blandet.`, { detailed: [`Revisiting ${skill.name} — earlier answers there were mixed.`, `Ser på ${skill.name} igjen — tidligere svar der var blandet.`] }), skillId: id, difficulty: clampD(skill, d), domainId: skill.domainId };
  }

  // 4. Boundary search: most informative unresolved domain/skill/difficulty.
  let best: { score: number; decision: AdaptationDecision } | null = null;
  for (const dom of Object.values(sub.domains)) {
    if (dom.resolved) continue;
    const skill = pickSkillForDomain(sub, dom, elig, views);
    if (!skill) {
      dom.resolved = true;
      continue;
    }
    const post = selectionPosterior(estimates[skill.id], dom.workingDifficulty);
    const d = clampD(skill, dom.workingDifficulty);
    const info = expectedInfoGain(post, d);
    const coverageBonus = dom.asked === 0 ? 0.05 : 0;
    const importance = Math.sqrt(sub.priority[dom.domainId] ?? 0.5);
    const score = (info * importance) / (1 + Math.max(0, dom.asked - 2) * 0.5) + coverageBonus;
    if (!best || score > best.score) {
      const lastStep = dom.successes > dom.failures ? 'up' : dom.failures > dom.successes ? 'down' : 'same';
      best = {
        score,
        decision: {
          reason: 'boundary', direction: lastStep,
          message: lastStep === 'up'
            ? LL(`You're doing well in ${catalog.domain(dom.domainId)!.name}. Level up: a harder task!`, `Du gjør det bra i ${catalog.domain(dom.domainId)!.name}. Opp et nivå: en vanskeligere oppgave!`, { detailed: [`You're handling ${catalog.domain(dom.domainId)!.name} well. Let's test a more demanding task.`, `Du klarer ${catalog.domain(dom.domainId)!.name} godt. La oss prøve en mer krevende oppgave.`] })
            : lastStep === 'down'
              ? L(`Narrowing down your level in ${catalog.domain(dom.domainId)!.name}.`, `Finner nivået ditt i ${catalog.domain(dom.domainId)!.name}.`)
              : L(`Looking for the edge of what you can do in ${catalog.domain(dom.domainId)!.name}.`, `Leter etter grensen for hva du får til i ${catalog.domain(dom.domainId)!.name}.`),
          skillId: skill.id, difficulty: d, domainId: dom.domainId,
        },
      };
    }
  }
  return best?.decision ?? null;
}

/** Update the adaptive search after an answer. Mutates and returns a copy. */
export function recordCalibrationAnswer(
  runIn: CalibrationRun, decision: AdaptationDecision, correct: boolean, misconceptionId: string | undefined,
  elig: EligibilityMap, views: Record<string, SkillView>, estimates: Record<string, SkillEstimate>,
): { run: CalibrationRun; next: AdaptationDecision['direction']; message: string; failureType?: FailureType; gap?: { skillId: string; prerequisiteId: string } } {
  const run: CalibrationRun = JSON.parse(JSON.stringify(runIn));
  const sub = currentSubject(run)!;
  const skill = catalog.skill(decision.skillId)!;
  const dom = sub.domains[skill.domainId] ?? (sub.domains[skill.domainId] = {
    domainId: skill.domainId, workingDifficulty: decision.difficulty, workingOffset: domainDistance(skill, elig), asked: 0, successes: 0, failures: 0, lo: null, hi: null, resolved: false,
  });
  sub.asked += 1;
  dom.asked += 1;
  sub.askedSkills[skill.id] = (sub.askedSkills[skill.id] ?? 0) + 1;
  const d = decision.difficulty;
  const level = d + 1.5 * domainDistance(skill, elig);
  let message = '';
  let failureType: FailureType | undefined;
  let gap: { skillId: string; prerequisiteId: string } | undefined;
  let next: AdaptationDecision['direction'] = 'same';

  if (correct) {
    dom.successes += 1;
    dom.lo = dom.lo === null ? level : Math.max(dom.lo, level);
    // resolve a pending failure on this skill: succeeded lower → the earlier task was simply hard
    const pend = sub.pendingFailure[skill.id];
    if (pend && d < pend.difficulty) {
      sub.failureTypes[skill.id] = pend.misconceptionId ? 'misconception' : 'difficult-task';
      delete sub.pendingFailure[skill.id];
    }
    // a passed prerequisite probe means the original failure was not a prerequisite gap
    if (decision.reason === 'prerequisite-probe') {
      for (const [sid, p] of Object.entries(sub.pendingFailure)) {
        if (catalog.prerequisites(sid).includes(skill.id)) {
          sub.failureTypes[sid] = p.misconceptionId ? 'misconception' : 'difficult-task';
          delete sub.pendingFailure[sid];
        }
      }
      message = LL(`${skill.name} is solid, so the task itself was hard — no missing basics.`, `${skill.name} sitter, så det var oppgaven som var vanskelig — ingen manglende grunnferdighet.`, { detailed: [`${skill.name} looks solid, so the earlier difficulty was the task itself — not a missing foundation.`, `${skill.name} sitter, så det var oppgaven som var vanskelig — ikke en manglende forkunnskap.`] });
      next = 'same';
    } else if (d >= 6) {
      const canStageUp = eligibleSkills(sub.subjectId, elig, dom.domainId).some((s) => domainDistance(s, elig) >= dom.workingOffset + 1);
      if (canStageUp) {
        dom.workingOffset += 1;
        dom.workingDifficulty = 4;
        message = LL('You\'re crushing these. Let\'s try a harder idea.', 'Du knuser disse. La oss prøve en vanskeligere idé.', { detailed: ["You're handling these problems well. Let's test a more advanced concept.", 'Du klarer disse oppgavene godt. La oss teste et mer avansert begrep.'] });
      } else if (d < 7) {
        dom.workingDifficulty = 7;
        message = LL('Strong result! One more step up: a hard challenge with no help.', 'Sterkt resultat! Ett steg til: en vanskelig utfordring uten hjelp.', { detailed: ['Strong result. One more step up: an advanced, unscaffolded challenge.', 'Sterkt resultat. Ett steg til: en avansert utfordring uten hjelp.'] });
      } else {
        dom.resolved = true;
        message = LL('This area is clearly strong — moving on.', 'Dette området er tydelig sterkt — vi går videre.', { detailed: ['This area is clearly strong — no need to spend more questions here.', 'Dette området er tydelig sterkt — ingen grunn til flere spørsmål her.'] });
      }
      next = 'up';
    } else {
      dom.workingDifficulty = Math.min(7, Math.max(dom.workingDifficulty, d) + 1);
      message = LL('Raising the difficulty: more steps, less help.', 'Øker vanskegraden: flere steg, mindre hjelp.', { detailed: ['Raising the demand: more steps, less scaffolding.', 'Øker vanskegraden: flere steg, mindre hjelp.'] });
      next = 'up';
    }
  } else {
    dom.failures += 1;
    dom.hi = dom.hi === null ? level : Math.min(dom.hi, level);
    if (misconceptionId) {
      sub.failureTypes[skill.id] = 'misconception';
    }
    if (decision.reason === 'prerequisite-probe') {
      // prerequisite confirmed weak → gap for the skill(s) that depended on it
      const owners = Object.keys(sub.pendingFailure).filter((sid) => catalog.prerequisites(sid).includes(skill.id));
      for (const sid of owners) {
        sub.failureTypes[sid] = 'prerequisite-gap';
        delete sub.pendingFailure[sid];
        sub.gaps.push({ skillId: sid, prerequisiteId: skill.id });
        gap = { skillId: sid, prerequisiteId: skill.id };
      }
      failureType = 'prerequisite-gap';
      // probe one level deeper if budget allows
      const depthOk = sub.asked < sub.maxQuestions - 2;
      const deeper = depthOk ? bestPrerequisite(skill.id, sub, elig, views) : null;
      if (deeper) {
        sub.pendingFailure[skill.id] = { difficulty: d, misconceptionId, stepDownTried: true };
        sub.probeQueue.unshift({ skillId: deeper.id, difficulty: Math.min(3, deeper.difficultyRange[1]), reason: 'prerequisite-probe', fromSkillId: skill.id });
      }
      message = LL('Looks like a missing piece. Studio will train it directly — the rest of the subject is not affected.', 'Det ser ut som en manglende brikke. Studio trener på den direkte — resten av faget påvirkes ikke.', { detailed: ['That looks like a foundation gap. Studio will target it directly — the rest of the subject is unaffected.', 'Det ser ut som et hull i grunnmuren. Studio øver på det direkte — resten av faget påvirkes ikke.'] });
      next = 'probe';
    } else {
      const pend = sub.pendingFailure[skill.id];
      if (!pend && d > 2) {
        sub.pendingFailure[skill.id] = { difficulty: d, misconceptionId, stepDownTried: false };
        sub.probeQueue.unshift({ skillId: skill.id, difficulty: d - 2, reason: 'step-down' });
        message = LL('One answer never decides anything — checking the same idea at an easier level.', 'Ett svar avgjør aldri noe — sjekker samme idé på et lettere nivå.', { detailed: ['One answer is never a verdict — checking the same idea at a more familiar level.', 'Ett svar avgjør aldri noe — sjekker samme idé på et mer kjent nivå.'] });
        next = 'down';
      } else {
        const prereq = bestPrerequisite(skill.id, sub, elig, views);
        sub.pendingFailure[skill.id] = { difficulty: pend?.difficulty ?? d, misconceptionId: pend?.misconceptionId ?? misconceptionId, stepDownTried: true };
        if (prereq && sub.asked < sub.maxQuestions) {
          sub.probeQueue.unshift({ skillId: prereq.id, difficulty: Math.min(3, prereq.difficultyRange[1]), reason: 'prerequisite-probe', fromSkillId: skill.id });
          message = LL('This area is unclear. Checking a basic skill first.', 'Dette området er uklart. Sjekker en grunnferdighet først.', { detailed: ['This area looks less certain. Checking a prerequisite before continuing.', 'Dette området er litt usikkert. Sjekker en forkunnskap før vi fortsetter.'] });
          next = 'probe';
        } else {
          sub.failureTypes[skill.id] = misconceptionId ? 'misconception' : 'insufficient-evidence';
          message = LL('Got it. Studio will check this again later instead of guessing.', 'Notert. Studio sjekker dette igjen senere i stedet for å gjette.', { detailed: ['Noted. Studio will gather more evidence here later rather than guess.', 'Notert. Studio samler mer informasjon her senere i stedet for å gjette.'] });
          next = 'down';
        }
      }
      dom.workingDifficulty = Math.max(1, dom.workingDifficulty - 1);
    }
  }

  // mixed evidence on a skill → revisit later
  const est = estimates[skill.id];
  if (est && est.evidenceCount >= 2 && sd(est.posterior) > 1.15 && !sub.revisit.includes(skill.id) && (sub.askedSkills[skill.id] ?? 0) < 2) {
    sub.revisit.push(skill.id);
  }

  // domain resolution
  const bracket = dom.lo !== null && dom.hi !== null && dom.hi - dom.lo <= 1.5;
  const capped = dom.asked >= 4;
  const ceiling = dom.successes >= 2 && dom.workingOffset >= 2;
  const floor = dom.failures >= 3;
  if ((bracket && dom.asked >= 2) || capped || ceiling || floor) dom.resolved = true;

  run.lastDecision = { ...decision, message };
  run.pending = undefined;
  return { run, next, message, failureType, gap };
}

/** Finalise a subject: unresolved pending failures become "insufficient evidence". */
export function completeSubject(
  runIn: CalibrationRun, views: Record<string, SkillView>, estimates: Record<string, SkillEstimate>, elig: EligibilityMap, now: number,
): { run: CalibrationRun; summary: CalibrationSummary } {
  const run: CalibrationRun = JSON.parse(JSON.stringify(runIn));
  const sub = currentSubject(run)!;
  for (const sid of Object.keys(sub.pendingFailure)) sub.failureTypes[sid] = sub.failureTypes[sid] ?? 'insufficient-evidence';
  sub.done = true;
  const domains = subjectReadiness(sub.subjectId, views, estimates, elig);
  const gaps = sub.gaps.map((g) => ({ skillId: g.prerequisiteId, blocks: catalog.descendants(g.prerequisiteId).filter((d) => {
    const r = views[d]?.relevance;
    return r === 'core' || r === 'extension';
  }) }));
  const summary: CalibrationSummary = {
    subjectId: sub.subjectId,
    completedAt: now,
    questions: sub.asked,
    selfEstimate: sub.selfEstimate,
    startDifficulty: sub.startDifficulty,
    domains,
    narrative: readinessNarrative(sub.subjectId, domains, elig[sub.subjectId].anchorLabel),
    failureTypes: sub.failureTypes,
    gaps,
  };
  run.current += 1;
  if (run.current >= run.subjectQueue.length) run.status = 'completed';
  return { run, summary };
}
